import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { safelyCreateHttpParams } from 'src/app/core/services/api/api-utils';
import { components, operations } from 'src/app/core/services/api/generated/ix-api.types';
import { IxRestApi } from 'src/app/core/services/api/ix-rest-api';
import { ExportToExcelService } from 'src/app/core/services/export-to-excel.service';
import { DeliverableApprovalWithError } from '../../shared/models/deliverables/deliverables.interface';

export type DeliverableDto = components['schemas']['DeliverableDto'];
export type DeliverablePropertyChangeEventOutputDto =
  components['schemas']['DeliverablePropertyChangeEventOutputDto'];
export type DeliverableTypeDto = components['schemas']['DeliverableTypeDto'];
export type DeliverableType = DeliverableTypeDto['type'];
export type DeliverableOverviewDto = components['schemas']['DeliverableOverviewDto'];
export type DeliverableOverviewStatusGroup = DeliverableOverviewDto['statusGroup'];

/*
export interface DeliverableType {
  id: number;
  name: string;
  type: DeliverableTypes;
  defaultValues: PropertyDefinitionDefaultValue[];
}*/

@Injectable({
  providedIn: 'root',
})
export class DeliverablesApiService {
  rowsToAdd$: Subject<number> = new Subject();
  readonly #baseUrl = '/api/deliverables/';
  readonly #http = inject(HttpClient);
  readonly #exportToExcelService = inject(ExportToExcelService);
  readonly #ixRestApi = inject(IxRestApi);

  getDeliverable(deliverableId: number) {
    return this.#ixRestApi.get({
      pathname: '/api/deliverables/{deliverableId}',
      options: { path: { deliverableId } },
    });
  }

  getDeliverableFull(path: operations['getDeliverableFull']['parameters']['path']) {
    return this.#ixRestApi.get({
      pathname: '/api/deliverables/{deliverableId}/deliverablefull',
      options: { path },
    });
  }

  getDeliverableOverviewsByPackage(
    path: operations['getDeliverableOverviewsByPackage']['parameters']['path'],
  ) {
    return this.#ixRestApi.get({
      pathname: '/api/deliverables/{packageId}/overviews',
      options: { path },
    });
  }

  createDeliverables(
    path: operations['createDeliverables']['parameters']['path'],
    query: operations['createDeliverables']['parameters']['query'],
  ) {
    return this.#ixRestApi.post(
      { pathname: '/api/deliverables/{packageId}/deliverables', options: { path } },
      undefined,
      { params: safelyCreateHttpParams(query) },
    );
  }

  updateDeliverableProperties(
    requestBody: operations['postDeliverablesEvents']['requestBody']['content']['application/json'],
  ) {
    requestBody.forEach(change => (change.viewType = 'DELIVERABLES_ADMIN'));
    return this.#ixRestApi
      .post({ pathname: '/api/propertychangeevents/deliverables', options: {} }, requestBody)
      .pipe(catchError((errorResponse: HttpErrorResponse) => of([errorResponse.error])));
  }

  approveDeliverable(path: operations['approveDeliverable']['parameters']['path']) {
    return this.#ixRestApi
      .put({ pathname: '/api/deliverables/{deliverableId}/approveDeliverable', options: { path } }, undefined)
      .pipe(
        catchError((error: DeliverableApprovalWithError) => {
          return of(error);
        }),
      );
  }

  deleteDeliverable(deliverableIds: number[]): Observable<any> {
    return this.#http.delete(this.#baseUrl, { body: deliverableIds });
  }

  exportToExcel(projectId: number) {
    const resourseUrl = `/api/projects/${projectId}/deliverables/excelexport`;
    return this.#exportToExcelService.export(resourseUrl);
  }
}
